import { FieldWrapper } from "@progress/kendo-react-form";
import {
  NumericTextBox,
  NumericTextBoxProps,
} from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import * as React from "react";
import FormLabel from "./FormLabel";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface FormNumericTextBoxProps
  extends Omit<NumericTextBoxProps, "name" | "value" | "onChange" | "valid"> {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
}

export const FormNumericTextBox: React.FC<FormNumericTextBoxProps> = ({
  name,
  validator,
  label,
  hint,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
  ...numericProps
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const inputId = name;
  const hintId = hint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";

  return (
    <FieldWrapper>
      <FormLabel
        id={label ? `${inputId}_label` : ""}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={field.value != null ? String(field.value) : undefined}
        placeholder={placeholder}
        optional={optional}
      >
        <NumericTextBox
          {...numericProps}
          name={name}
          disabled={disabled}
          valid={!fieldState.error}
          value={(field.value as number | null | undefined) ?? null}
          onChange={e => field.onChange(e.value)}
          onBlur={field.onBlur}
        />
        {hint && <Hint id={hintId}>{hint}</Hint>}
        {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormNumericTextBox;
